/**
 * Work Schedule Service
 *
 * API service for managing work schedules - employee job assignments and station schedules.
 * Handles date format conversion between frontend (YYYY-MM-DD) and ICS (YYYYMMDD).
 */
import api from './api';

// Job type from ics.hte_production_job table (ICS-aligned)
export interface JobType {
  job_code: string;
  job_description: string;
  short_description: string;
}

// Employee from auth.users table
export interface Employee {
  user_id: string;
  full_name: string;
  employee_number: string;
  disabled: string | null;
}

// Job assignment from ics.hte_production_job_assignment (ICS-aligned)
export interface JobAssignment {
  employee_number: string;
  job_code: string;
  assignment_date: string; // YYYY-MM-DD format (converted from m_date)
  m_time: string;
  operator_id: string;
}

// Station schedule from ics.hte_station_schedule (HTE-only)
export interface StationSchedule {
  id: string;
  station_id: number;
  station_name: string;
  work_date: string; // YYYY-MM-DD format
  created_by: string;
}

// Operator station assignment from ics.hte_operator_station_assignment (HTE-only)
export interface OperatorStationAssignment {
  id: string;
  employee_number: string;
  station_id: number;
  station_name: string;
  work_date: string; // YYYY-MM-DD format
}

// Station info
export interface Station {
  station_id: number;
  station_name: string;
}

// Daily summary
export interface DailySummary {
  date: string;
  operators: number;
  packers: number;
  stations: number;
}

// Weekly schedule response (matches backend response structure)
export interface WeeklyScheduleResponse {
  startDate: string;
  endDate: string;
  employees: Employee[];
  assignments: JobAssignment[];
  stations: Station[];
  stationSchedules: StationSchedule[];
  operatorStationAssignments: OperatorStationAssignment[];
  jobTypes: JobType[];
  dailySummaries: Record<string, DailySummary>;
}

// Manpower summary for production session creation
export interface ManpowerSummary {
  date: string;
  summary: {
    operators: number;
    packers: number;
    stations: number;
  };
  operators: Array<{
    employee_number: string;
    job_code: string;
    m_date: string;
    m_time: string;
    full_name: string;
    role_name: string;
  }>;
  packers: Array<{
    employee_number: string;
    job_code: string;
    m_date: string;
    m_time: string;
    full_name: string;
    role_name: string;
  }>;
  stations: Array<{
    station_id: number;
    station_name: string;
    station_type?: string;
  }>;
}

// Scheduled operator with station assignment and availability status
export interface ScheduledOperator {
  employee_number: string;
  job_code: string;
  full_name: string;
  station_id: number | null;
  station_name: string | null;
  is_scheduled: boolean;
  is_assigned: boolean;
  assignment_status: string | null;
  assignment_station_id: number | null;
}

// Scheduled packer with availability status
export interface ScheduledPacker {
  employee_number: string;
  job_code: string;
  full_name: string;
  is_scheduled: boolean;
  is_assigned: boolean;
  assignment_status: string | null;
}

// Scheduled workforce response from /scheduled-workforce/:date endpoint
export interface ScheduledWorkforceResponse {
  date: string;
  operators: ScheduledOperator[];
  packers: ScheduledPacker[];
  stations: Array<{
    station_id: number;
    station_name: string;
  }>;
  summary: {
    total_operators: number;
    available_operators: number;
    assigned_operators: number;
    total_packers: number;
    available_packers: number;
    assigned_packers: number;
    total_stations: number;
  };
}

const workScheduleService = {
  // Get weekly schedule data
  async getWeeklySchedule(startDate: string): Promise<WeeklyScheduleResponse> {
    const response = await api.get(`/operator/work-schedules/week?startDate=${startDate}`);
    // API returns data directly, not nested
    return response.data;
  },

  // Get manpower summary for a specific date
  async getManpowerSummary(date: string): Promise<ManpowerSummary> {
    const response = await api.get(`/operator/work-schedules/manpower/${date}`);
    // API returns data directly, not nested
    return response.data;
  },

  // Get all job types
  async getJobTypes(): Promise<JobType[]> {
    const response = await api.get('/operator/work-schedules/job-types');
    // API returns array directly, not nested in data
    return response.data;
  },

  // Create or update a job assignment
  async createAssignment(data: {
    employee_number: string;
    job_code: string;
    assignment_date: string; // YYYY-MM-DD format
    operator_id?: string;
  }): Promise<JobAssignment> {
    const response = await api.post('/operator/work-schedules', data);
    return response.data.assignment;
  },

  // Bulk create/update assignments
  async bulkCreateAssignments(assignments: Array<{
    employee_number: string;
    job_code: string;
    assignment_date: string;
    operator_id?: string;
  }>): Promise<{ count: number; assignments: JobAssignment[] }> {
    const response = await api.post('/operator/work-schedules/bulk', { assignments });
    return response.data;
  },

  // Delete assignment by employee/date/job
  async deleteAssignment(data: {
    employee_number: string;
    assignment_date: string;
    job_code: string;
  }): Promise<void> {
    await api.delete('/operator/work-schedules/by-details', { data });
  },

  // Copy week schedule to another week
  async copyWeek(
    sourceStartDate: string,
    targetStartDate: string
  ): Promise<{ copiedAssignments: number; copiedStationSchedules: number; copiedOperatorStationAssignments: number }> {
    const response = await api.post('/operator/work-schedules/copy-week', {
      sourceStartDate,
      targetStartDate,
    });
    return response.data;
  },

  // Add station to schedule
  async addStationSchedule(stationId: number, workDate: string, createdBy?: string): Promise<StationSchedule> {
    const response = await api.post('/operator/work-schedules/stations', {
      station_id: stationId,
      work_date: workDate,
      created_by: createdBy,
    });
    return response.data.stationSchedule;
  },

  // Remove station from schedule by ID
  async removeStationSchedule(id: string): Promise<void> {
    await api.delete(`/operator/work-schedules/stations/${id}`);
  },

  // Remove station by station_id and work_date
  async removeStationScheduleByDetails(stationId: number, workDate: string): Promise<void> {
    await api.delete('/operator/work-schedules/stations/by-details', {
      data: { station_id: stationId, work_date: workDate },
    });
  },

  // Bulk update station schedules for a date
  // NOTE: The API expects work_date and station_ids, not individual schedules with active flags
  async bulkUpdateStationSchedules(
    workDate: string,
    stationIds: number[],
    createdBy?: string
  ): Promise<{ count: number; stationSchedules: StationSchedule[] }> {
    const response = await api.post('/operator/work-schedules/stations/bulk', {
      work_date: workDate,
      station_ids: stationIds,
      created_by: createdBy,
    });
    return response.data;
  },

  // Assign or update operator to station for a date
  async assignOperatorToStation(data: {
    employee_number: string;
    station_id?: number | null;
    station_ids?: number[];
    work_date: string;
    created_by?: string;
  }): Promise<OperatorStationAssignment[] | OperatorStationAssignment | null> {
    const response = await api.post('/operator/work-schedules/operator-stations', data);
    return response.data.assignments || response.data.assignment || null;
  },

  // Remove operator station assignment
  async removeOperatorStationAssignment(employeeNumber: string, workDate: string): Promise<void> {
    await api.delete('/operator/work-schedules/operator-stations/by-details', {
      data: { employee_number: employeeNumber, work_date: workDate },
    });
  },

  // Get scheduled workforce for a date (operators/packers with assignment status)
  async getScheduledWorkforce(date: string): Promise<ScheduledWorkforceResponse> {
    const response = await api.get(`/operator/work-schedules/scheduled-workforce/${date}`);
    return response.data;
  },
};

export default workScheduleService;
